/* eslint-disable no-nested-ternary */
import { Button, CircularProgress, Grid, Icon, makeStyles, Paper, Theme, Typography } from '@material-ui/core';
import { useEffect, useState, useCallback, useContext, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { AppAPI, WebSocketFactory, WebSocketObserver, ModeWebSocketEvent, isModeWebSocketEvent } from '@moderepo/mode-apis';
import { ritzDataContext, selectAgentLoginInfo, selectSudoProjectAccessKey } from '@moderepo/mode-data-state-ritz';
import moment from 'moment';
import { ApiErrorCode, DeviceCommand, RitzWebSocketFactory } from '@moderepo/mode-internal-apis';
import { modeUIContext, showErrorNotification } from '@moderepo/mode-ui-state';
import { getErrorMessage, Time } from '@moderepo/mode-app-components';
import { EntityUIComponentProps, ExtGrid } from '../../../..';
import { useSelectEntity, useSelectProject } from '../../../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../../../utils/ritzComponentsCollection';
import { EntityUIComponentCollection, ENTITY_DEVICE_ID_ATTRIBUTE, GridCustomSettings, isGridCustomSettings } from '../../..';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingNumberValue, isValidSettingStringValue } from '../../../../../utils/utils';
import { SettingsErrorLogger } from '../../../../../utils/logger';
import { ActlyzerCameraCard } from './ActlyzerCameraCard';
import { ActlyzerCameraInfo, ActlyzerCameraStreamState, ActlyzerDeviceStatus } from '.';
import { isPeripheralDeviceCommandSettings, PeripheralDeviceCommandSettings } from '../../../../devices';
import { useRitzTranslation } from '../../../../../utils/customHooks';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        videoCard: {
            height: '100%',
        },

        notStreamingMessage: {
            width         : '100%',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            flexDirection : 'column',
            padding       : theme.spacing(3),

            '& .icon': {
                fontSize: 50,
            },

            '& .message': {
                marginTop: theme.spacing(2),
            },

            '& .action': {
                marginTop: theme.spacing(2),
            },
        },
    };
}, { name: 'ActlyzerEntityCamerasUIComponent', index: 1 });


// When the device is in STARTING state, we have to keep checking the device status until it changed to RUNNING or other state so that we know
// what to do next. To prevent the UI from keep sending GET STATUS command to the device, we will add a delay and only send the command once
// every CHECK_STATUS_DELAY_IN_MS.
const GET_STATUS_DELAY_IN_SECS = 3;


// When the user leave the page, we will send a command to stop streaming automatically. But we will delay it by this number of seconds
// before sending the command incase the user come back to the page. The default delay will be 3 minutes if the component is not configured
// to use a different delay
const SEND_STOP_COMMAND_DEFAULT_DELAY_IN_SECONDS = Time.MINUTE_IN_SECS * 3;

// The value we will use to tell the component NOT to send stop command
const DISABLE_SEND_STOP_COMMAND_SETTING_VALUE = -1;


/**
 * The interface of the response result object we get from the web socket when we send commands to the device. This does not have all the possible
 * props that can be returned by the backend but only the props that the UI need. For example
 *
 *      result = {
 *          act_main_status: "READY",
 *          code: 0,
 *          message: "..."
 *      }
 *
 *      result = {
 *          act_main_status: "RUNNING",
 *          code: 0,
 *          info: "..."
 *      }
 */
interface CommandResponseResult {
    readonly act_main_status?: ActlyzerDeviceStatus | undefined;
    readonly error?: string | undefined;
    readonly code?: number | undefined;
    readonly message?: string | undefined;
    readonly info: readonly number[] | string;
}


/**
 * This is the partial interface of the message data return from the device through the web socket. This is NOT the complete list of
 * attributes. These are the only attributes that the front end needs.
 */
interface ActlyzerVideoMessageData {
    readonly Payload: {
        // The array of camera IDs. The front end will use this to determine how many cameras there are
        readonly CameraID: readonly number[];
        readonly MaxCamera: number;
        readonly Results: readonly {
            readonly FrameInfo: {
                readonly Image: string;
                readonly time: string;
                readonly TransferWidth: number;
                readonly TransferHeight: number;
                readonly cam_id: number;
                readonly frame_number: number;
                readonly frame_interval: number;
                readonly fps: number;
                readonly sequence_number: number;
                readonly image_index: number
            };
            readonly ScenarioRecog?: {
                // The array of visitors
                readonly BehaviorArray?: readonly object[] | undefined;
            } | undefined
        }[];
    }
}


/**
 * Type guard function to check if an object is an ActlyzerVideoMessageData. An ActlyzerVideoMessageData has many attributes but we will not
 * check everything. We only need to check the attributes that we use.
 */
const isActlyzerVideoMessageData = (object: unknown): object is ActlyzerVideoMessageData => {
    const data = object as ActlyzerVideoMessageData;

    if (!isObjectLiteral(data)) {
        return false;
    }

    if (!isObjectLiteral(data.Payload)) {
        return false;
    }

    // CameraID is required and it must be array
    if (!data.Payload.CameraID || !(data.Payload.CameraID instanceof Array)) {
        return false;
    }

    // CameraID must be array of numbers
    if (!data.Payload.CameraID.every((value) => {
        return typeof value === 'number';
    })) {
        return false;
    }

    // Payload.results must be an array
    if (!data.Payload.Results) {
        return false;
    }

    // And each items in the Payload.results must be objects that contains the required attributes.
    if (!data.Payload.Results.every((result) => {
        if (!isObjectLiteral(result.FrameInfo)) {
            return false;
        }

        if (!isValidSettingStringValue(result.FrameInfo.Image, true)) {
            return false;
        }

        if (!isValidSettingStringValue(result.FrameInfo.time, true)) {
            return false;
        }

        if (!isValidSettingNumberValue(result.FrameInfo.cam_id, true, false)) {
            return false;
        }

        // ScenarioRecog is not required but if there is one, it must be valid
        if (result.ScenarioRecog !== undefined) {

            // ScenarioRecog must be an Object
            if (!isObjectLiteral(result.ScenarioRecog)) {
                return false;
            }

            // BehaviorArray can be undefined or must be an Array if it is not undefined
            if (result.ScenarioRecog.BehaviorArray !== undefined && !(result.ScenarioRecog.BehaviorArray instanceof Array)) {
                return false;
            }
        }

        return true;
    })) {
        return false;
    }

    return true;
};



/**
 * Check if the given event object is the the response for the specified commandSettings. Because the device web socket receive event for many
 * different things so to know for sure if the data we received is the response for a specific command, we need to compare some of the
 * event data attributes with the command's attributes
 */
const isResponseForCommand = (
    event: ModeWebSocketEvent,
    homeId: number,
    deviceId: number,
    commandSettings: PeripheralDeviceCommandSettings,
): boolean => {

    // the deviceIds don't match which mean this event is for something else.
    if (event.homeId !== homeId || event.originDeviceId !== deviceId) {
        return false;
    }

    // Make sure the event.eventType is the same as the expectedCommandResponse.eventType
    if (event.eventType !== commandSettings.action) {
        return false;
    }

    // Now we look inside the eventData. eventData structure is unknown so we need to use the command settings to determine if the event is
    // for the command we just sent. We only need to do this if 'eventData' is an OBJECT and we have 'responseActionFieldName' setting.
    const eventData = event.eventData;
    if (isObjectLiteral(eventData) && commandSettings?.responseActionFieldName) {
        const actionField = commandSettings.responseActionFieldName;

        // See if the eventData's value for the given actionField is not the same as the command we sent recently then we can assume the
        // event is not for the command.
        if (eventData[actionField] !== commandSettings.command) {
            return false;
        }
    }

    return true;
};


/**
 * Get the result object from the web socket event data based on the provided commandSettings. Different command might different result
 * so this will get the result based on the commandSettings.responseResultFieldName. For example, some command might get the result from the
 * event data's "result" attribute like this. The "responseResultFieldName" is set to "result"
 *      event: {
 *          eventData: {
 *              result: {...}
 *          }
 *      }
 *
 * And some command might get result from the event data's "data", the "responseResultFieldName" is set to "data"
 *      event: {
 *          eventData: {
 *              data: {...}
 *          }
 *      }
 */
const getCommandResponseResult = (
    event: ModeWebSocketEvent,
    commandSettings: PeripheralDeviceCommandSettings | undefined,
): CommandResponseResult | undefined => {
    const eventData = event.eventData;
    if (isObjectLiteral(eventData) && commandSettings && commandSettings.responseResultFieldName) {
        return eventData[commandSettings.responseResultFieldName];
    }
    return undefined;
};


interface CommandInfo {
    readonly homeId: number
    readonly deviceId: number;
    readonly payload: DeviceCommand;
    readonly settings: PeripheralDeviceCommandSettings;
}


/**
 * There are a few places where we need to send a command to the backend. Each time we need to send a command, we need to create a Payload
 * object based on the command's settings. The payload objects should be the same for the same command therefore there is no need to
 * create a new one every time. Therefore, this helper function is used for creating the payload for a command ahead of time. We will cache
 * this object and reuse it each time we need to send a command.
 */
const createCommandInfo = (
    homeId: number | undefined,
    deviceId: number | undefined,
    commandSettings: PeripheralDeviceCommandSettings | undefined,
): CommandInfo | undefined => {
    if (!homeId || !deviceId || !commandSettings) {
        return undefined;
    }

    return {
        deviceId,
        homeId,
        payload: {
            action    : commandSettings.action,
            parameters: {
                destination: commandSettings.destinationTemplate.replaceAll('{{deviceId}}', deviceId.toString()),
                action     : commandSettings.command,
                parameters : commandSettings.defaultParameters,
            },
        },
        settings: commandSettings,
    };
};


/**
 * Update the provided cameras info with the new video data from the back end and return the updated cameras info
 */
const updateCamerasData = (
    currentCameras: readonly ActlyzerCameraInfo[] | undefined,
    newVideoData: ActlyzerVideoMessageData,
): readonly ActlyzerCameraInfo[] => {

    /**
     * newVideoData.Payload.CameraID contains the list of camera IDs of the cameras that are streaming. This list might not be the same as the
     * currentCameras list we have in the front end. There might be some new cameras added to the stream or some are removed. Therefore,
     * we have to go through this list of CameraID and create 1 Camera object for each ID in the newVideoData.Payload.CameraID if we don't
     * already have one.
     */
    const newCameras = newVideoData.Payload.CameraID.map((camId): ActlyzerCameraInfo => {

        // First, check if there is already a Camera object in the currentCameras with the given camId
        const existingCamera = currentCameras?.find((camera) => {
            return camera.cameraId === camId;
        });

        // If there is already a Camera object for the given camId, then just return that one, don't need to create a new
        // camera object
        if (existingCamera) {
            return existingCamera;
        }

        // Else, create a new camera
        return {
            cameraId         : camId,
            streamingState   : ActlyzerCameraStreamState.NOT_STREAMING,
            currentVideoFrame: undefined,
        };
    });

    // For each camera, get the video image for that camera from the "newVideoData" and update the camera's image queue
    return newCameras.map((cameraInfo) => {
        const allFrames = newVideoData.Payload.Results;

        if (allFrames) {
            // the frames array send in the websocket might not be in the same order as the newCameras array. We can't assume
            // the data in allFrames[i] is for newCameras[i] and take the video image from the
            // allFrames[i] to assign to newCameras[i]. We need to match the "cameraInfo.cameraId" with the
            // "cam_id" in the frameInfo.cam_id.
            const matchedFrameInfo = allFrames.find((oneFrameInfo) => {
                return cameraInfo.cameraId === oneFrameInfo.FrameInfo.cam_id;
            });

            if (matchedFrameInfo) {
                const image = `data:image/jpg;base64,${matchedFrameInfo.FrameInfo.Image}`;
                const timestamp = moment(matchedFrameInfo.FrameInfo.time);

                // We have received video from the web socket. We can now change the camera's stream state to STREAMING
                // BUT only if it is NOT in the ON_PAUSE state. If it is in the ON_PAUSE state then keep the current state.
                const newStreamingState = cameraInfo.streamingState !== ActlyzerCameraStreamState.ON_PAUSE
                    ? ActlyzerCameraStreamState.STREAMING
                    : cameraInfo.streamingState;            // Keep the current state

                return {
                    ...cameraInfo,
                    streamingState   : newStreamingState,
                    currentVideoFrame: {
                        image,
                        timestamp: timestamp.valueOf(),
                        headCount: matchedFrameInfo.ScenarioRecog?.BehaviorArray
                            ? matchedFrameInfo.ScenarioRecog.BehaviorArray.length : undefined,
                    },
                };
            }
        }

        return cameraInfo;
    });
};



/**
 * This is the interface for the ActlyzerEntityCamerasUIComponent's CustomSettings.
 */
interface ActlyzerEntityCamerasUIComponentCustomSettings extends GridCustomSettings {
    // Because the component needs to send command to the device to start/end/get streaming status, the component will need to know
    // how to send these commands. The admin must provide the configuration for these commands
    readonly startStreamingCommand: PeripheralDeviceCommandSettings;
    readonly stopStreamingCommand: PeripheralDeviceCommandSettings;
    readonly getStreamingStatusCommand: PeripheralDeviceCommandSettings;

    // When a device is requested to START streaming, it will not start right away and the front end will need to keep sending GET STATUS
    // command to the device to check the status. This delay option controls how many seconds to wait in between each command.
    // The default is 3 seconds.
    readonly checkStatusDelayInSecs?: number | undefined;

    // How long to wait before sending a STOP streaming command to the device after the user leave that page.
    // The value must be between 10 sec and 300 secs (5 mins). To have the component NOT sending the STOP command then set the value to -1
    readonly sendStopCommandDelayInSecs?: number | undefined;

    // To specify the rate to display the video frames. Sometimes the frames come to the web socket in different rate than the camera's
    // frameRate. For example, they can come in with 30 to 40 frames within a couple of seconds and then stop for a few seconds and then
    // another batch come in. If we show the frames as soon as they arrive over the web socket, it might not look smooth. Therefore, this
    // option allows the admin to control how fast we show the frames. If this is not specified, the component will show the frames as soon
    // as they arrive. If this is specified, the frames will be show at the specified rate doesn't matter how fast they come in.
    readonly frameRate?: number | undefined;

    readonly showVisitorsCount?: boolean | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of ActlyzerEntityCamerasVideoStreamUIComponentCustomSettings
 */
const isActlyzerEntityCamerasUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is ActlyzerEntityCamerasUIComponentCustomSettings => {
    const settings = obj as ActlyzerEntityCamerasUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // Make sure the admin provided the configuration for the commands we need to use for starting, ending, and checking streaming status
    if (!isPeripheralDeviceCommandSettings(settings.startStreamingCommand, errorLogger, [...configPath, 'startStreamingCommand'])) {
        return false;
    }

    if (!isPeripheralDeviceCommandSettings(settings.stopStreamingCommand, errorLogger, [...configPath, 'stopStreamingCommand'])) {
        return false;
    }

    if (!isPeripheralDeviceCommandSettings(settings.getStreamingStatusCommand, errorLogger, [...configPath, 'getStreamingStatusCommand'])) {
        return false;
    }

    if (!isValidSettingNumberValue(settings.checkStatusDelayInSecs, false, false, 3, 30)) {
        return errorLogger?.logError({
            message: '"checkStatusDelayInSecs" is must be undefined or an integer between 3 and 30.',
            value  : settings.checkStatusDelayInSecs,
            path   : [...configPath, 'checkStatusDelayInSecs'],
        }) ?? false;
    }

    // The min and max delay is 10 seconds and 5 minutes
    const minDelayInSec = 10;
    const maxDelayInSec = Time.MINUTE_IN_SECS * 5;
    if (!isValidSettingNumberValue(settings.sendStopCommandDelayInSecs, false, false, minDelayInSec, maxDelayInSec)
        && settings.sendStopCommandDelayInSecs !== DISABLE_SEND_STOP_COMMAND_SETTING_VALUE) {
        return errorLogger?.logError({
            // eslint-disable-next-line max-len
            message: `"sendStopCommandDelayInSecs" is must be undefined or an integer between ${minDelayInSec} and ${maxDelayInSec} or value ${DISABLE_SEND_STOP_COMMAND_SETTING_VALUE}.`,
            value  : settings.sendStopCommandDelayInSecs,
            path   : [...configPath, 'sendStopCommandDelayInSecs'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.frameRate, false, false, 1, 120)) {
        return errorLogger?.logError({
            message: '"frameRate" is must be undefined or an integer between 1 and 120.',
            value  : settings.frameRate,
            path   : [...configPath, 'frameRate'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showVisitorsCount)) {
        return errorLogger?.logError({
            message: '"showVisitorsCount" is must be undefined or a boolean.',
            value  : settings.showVisitorsCount,
            path   : [...configPath, 'showVisitorsCount'],
        }) ?? false;
    }

    // Because this CustomSettings interface extends GridCustomSettings interface, we can call the "isGridCustomSettings" to validate the
    // gridSizes settings
    if (!isGridCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



/**
 * We need to create tasks to be executed at some time later AFTER the user leaves the component. These tasks are for sending STOP command
 * to the device. This structure is for storing the information we need to the task details.
 */
interface StopStreamingTask {
    // The ID of the timer that is created to execute this task. "undefined" means this task has not been scheduled yet
    readonly timerId?: number | undefined;

    // The command payload
    readonly commandInfo: CommandInfo;
}


/**
 * A map of device ID => StopStreamingTask to be executed. The reason we need to group by ID is because there can be more than 1 devices that
 * support video streaming.
 * This Object is global in this "ActlyzerEntityCamerasUIComponent" scope only and will persist for the entire session of the app.
 */
const stopStreamingTasksByDeviceId: {
    [deviceId: string]: StopStreamingTask | undefined;
} = {};



interface ActlyzerEntityCamerasUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: ActlyzerEntityCamerasUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing Cameras belonged to an Entity. It will show the cameras and provide options for the user
 * to stream videos from the camera.
 */
export const ActlyzerEntityCamerasUIComponent: React.FC<ActlyzerEntityCamerasUIComponentProps> = (
    props: ActlyzerEntityCamerasUIComponentProps,
) => {

    const { state:dataState } = useContext(ritzDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const classes = useStyle();
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const agentLoginInfo = selectAgentLoginInfo(dataState);
    const projectKey = selectSudoProjectAccessKey(dataState, props.projectId);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);


    /**
     * Create a ActlyzerCameraInfo for each camera in the "camerasSettings"
     */
    const [cameras, setCameras] = useState<readonly ActlyzerCameraInfo[] | undefined>();


    /**
     * Pre-create the startCommand payload so that we can use it later without having to create it every time we need to send the command to
     * the backend.
     */
    const startCommandInfo = useMemo(() => {
        return createCommandInfo(
            selectedEntity?.homeId,
            selectedEntity?.attributes?.[ENTITY_DEVICE_ID_ATTRIBUTE] as number,
            props.customSettings?.startStreamingCommand,
        );
    }, [props.customSettings?.startStreamingCommand, selectedEntity?.attributes, selectedEntity?.homeId]);


    /**
     * Do the same for the STOP command
     */
    const stopCommandInfo = useMemo(() => {
        return createCommandInfo(
            selectedEntity?.homeId,
            selectedEntity?.attributes?.[ENTITY_DEVICE_ID_ATTRIBUTE] as number,
            props.customSettings?.stopStreamingCommand,
        );
    }, [props.customSettings?.stopStreamingCommand, selectedEntity?.attributes, selectedEntity?.homeId]);


    /**
     * Same for the GET STATUS command
     */
    const statusCommandInfo = useMemo(() => {
        return createCommandInfo(
            selectedEntity?.homeId,
            selectedEntity?.attributes?.[ENTITY_DEVICE_ID_ATTRIBUTE] as number,
            props.customSettings?.getStreamingStatusCommand,
        );
    }, [props.customSettings?.getStreamingStatusCommand, selectedEntity?.attributes, selectedEntity?.homeId]);


    // The state to keep track of whether the user specifically initiated video streaming. This is to prevent the user from seeing the cameras
    // automatically go into stream mode without taking any action which seem weird. This can happen because another user on another computer
    // started streaming.
    const [streamVideoInitiated, setStreamVideoInitiated] = useState(false);


    // This state is for keeping track of the device's streaming status. When we send a command to the device to START/STOP, the device status
    // will change so we need to keep track of the status so we know what to show in the UI.
    const [deviceStatus, setDeviceStatus] = useState<ActlyzerDeviceStatus>(ActlyzerDeviceStatus.READY);


    /**
     * A map of commands to be sent using the "action" string as the key. This is for keeping track of the command we are going to send
     * to prevent from sending the same command multiple times in a short period. This can happen when there is another user from another
     * computer that also try to stream video for the same device. Whenever that user send a command to check the device status, the current
     * user will get that event and then will start sending GET STATUS command every N seconds to check the device status. These 2 users
     * will cause this component from sending many GET STATUS commands.
     * So before we send a command, we will add that command to the schedule using the command's "action" as key. This way, if the
     * sendCommand function is called again for the same action, it will replace the previous command in the schedule.
     * This is what the map will look like
     *      sendCommandSchedule = {
     *          POST_to_start: {command payload},
     *          POST_to_stop: {command payload},
     *          GET_from_status: {command payload}
     *      }
     */
    const sendCommandSchedule = useRef(new Map<string, DeviceCommand>());

    const sendCommandFromSchedule = useCallback(async (deviceId: number, commandAction: string) => {
        // Find the command for the given commandAction from the schedule
        const commandPayload = sendCommandSchedule.current.get(commandAction);

        // Now we can delete it from the schedule and then make an API call to send the command
        sendCommandSchedule.current.delete(commandAction);
        if (commandPayload) {
            await AppAPI.getInstance().sendCommandToDevice(deviceId, commandPayload);
        }
    }, []);


    /**
     * Send a command to the device in the next "delayInSeconds".
     */
    const sendCommand = useCallback(async (deviceId: number, commandAction: string, commandPayload: DeviceCommand, delayInSeconds: number = 0) => {
        if (deviceId) {

            // Check if there is a command with the same "action" already in the schedule. If there is one, don't need to do anything
            // For example: If someone call this function to send a command with action "START" but there is already a command
            // in the schedule with the same action "START", we will ignore this new request.
            if (sendCommandSchedule.current.has(commandAction)) {
                return;
            }

            // Add the command to the schedule so that if someone call this function for the same command action then it will ignore it
            sendCommandSchedule.current.set(commandAction, commandPayload);

            if (delayInSeconds && delayInSeconds > 0) {
                // If a delay value is specified, delay and then send.
                setTimeout(async () => {
                    sendCommandFromSchedule(deviceId, commandAction);
                }, delayInSeconds * 1000);
            } else {
                sendCommandFromSchedule(deviceId, commandAction);
            }
        }
    }, [sendCommandFromSchedule]);


    /**
     * Change the specified camera state. If a cameraId is not provided then all the cameras will be updated.
     */
    const changeCamerasState = useCallback((state: ActlyzerCameraStreamState, targetCameraId?: number) => {
        setCameras((currentCameras) => {
            return currentCameras?.map((cameraInfo) => {
                if (targetCameraId === undefined || cameraInfo.cameraId === targetCameraId) {
                    return {
                        ...cameraInfo,
                        streamingState: state,
                    };
                }
                // No change, return the same object.
                return cameraInfo;
            });
        });
    }, []);



    /**
     * Whenever we receive "error" message inside the "result" data from the device web socket, we will display this error message
     */
    const handleDeviceErrorStatus = useCallback(() => {
        // We received "error" from the web socket result.
        setDeviceStatus(ActlyzerDeviceStatus.READY);
        changeCamerasState(ActlyzerCameraStreamState.NOT_STREAMING);
        uiDispatch(showErrorNotification(getErrorMessage(ApiErrorCode.NETWORK_ERROR)));
    }, [changeCamerasState, uiDispatch]);


    /**
     * This WebSocket observer is for observing messages from the web socket that is used for sending command to the device to start, end,
     * and check streaming status.
     */
    const deviceWebsocketObserver = useMemo((): WebSocketObserver => {
        return async (event: ModeWebSocketEvent | unknown) => {

            if (!startCommandInfo || !stopCommandInfo || !statusCommandInfo || !isModeWebSocketEvent(event)) {
                return;
            }

            /**
             * We received a message from the device web socket. We don't know what this message is for therefore we need to check to see
             * if it is a message response for one of the commands that we send to the device.
             */

            if (isResponseForCommand(event, startCommandInfo.homeId, startCommandInfo.deviceId, startCommandInfo.settings)) {
                // If the response is for the START streaming command

                // Get the status from the event's result
                const result = getCommandResponseResult(event, startCommandInfo.settings);
                const status = result?.act_main_status;
                const errorMessage = result?.error;

                if (status === ActlyzerDeviceStatus.ERROR || errorMessage) {
                    handleDeviceErrorStatus();
                    return;
                }

                if (status !== undefined) {
                    setDeviceStatus(status);
                }

                if (status === ActlyzerDeviceStatus.RUNNING) {
                    // A START command is sent and the current status is RUNNING which means the device is streaming videos. We can change the
                    // camera's status to WAITING_FOR_VIDEO first. Once we received a video, we will change its state to PLAYING
                    changeCamerasState(ActlyzerCameraStreamState.WAITING_FOR_VIDEO);
                } else {
                    // Event though we know the START streaming command is sent BUT the status is NOT RUNNING. Therefore, we can assume the
                    // device hasn't started streaming yet. We will set the camera status so CONNECTING and send a GET STATUS command to check
                    // the status
                    changeCamerasState(ActlyzerCameraStreamState.START_STREAMING_REQUESTED);
                    sendCommand(
                        statusCommandInfo.deviceId,
                        statusCommandInfo.settings.command,
                        statusCommandInfo.payload,
                        props.customSettings?.checkStatusDelayInSecs ?? GET_STATUS_DELAY_IN_SECS,
                    );
                }

            } else if (isResponseForCommand(event, stopCommandInfo.homeId, stopCommandInfo.deviceId, stopCommandInfo.settings)) {
                // If the response is for the STOP streaming command

                // Get the status from the event's result and update the local deviceStatus
                const result = getCommandResponseResult(event, stopCommandInfo.settings);
                const status = result?.act_main_status;
                const errorMessage = result?.error;

                if (status === ActlyzerDeviceStatus.ERROR || errorMessage) {
                    handleDeviceErrorStatus();
                    return;
                }

                if (status !== undefined) {
                    setDeviceStatus(status);
                }

                if (status === ActlyzerDeviceStatus.READY) {
                    changeCamerasState(ActlyzerCameraStreamState.NOT_STREAMING);
                } else {
                    // Even though we know a STOP command is sent but if the status is NOT READY then we can't assume streaming stopped.
                    // We will set the camera state to DISCONNECTING for now and check the device status again
                    changeCamerasState(ActlyzerCameraStreamState.STOP_STREAMING_REQUESTED);
                    sendCommand(
                        statusCommandInfo.deviceId,
                        statusCommandInfo.settings.command,
                        statusCommandInfo.payload,
                        props.customSettings?.checkStatusDelayInSecs ?? GET_STATUS_DELAY_IN_SECS,
                    );
                }

            } else if (isResponseForCommand(event, statusCommandInfo.homeId, statusCommandInfo.deviceId, statusCommandInfo.settings)) {
                // If the response if for the GET STATUS command

                // Get the status from the event's result
                const result = getCommandResponseResult(event, statusCommandInfo.settings);
                const status = result?.act_main_status;
                const errorMessage = result?.error;

                if (status === ActlyzerDeviceStatus.ERROR || errorMessage) {
                    handleDeviceErrorStatus();
                    return;
                }

                if (status !== undefined) {
                    setDeviceStatus(status);
                }

                if (status === ActlyzerDeviceStatus.RUNNING) {
                    // If the current status is RUNNING then we will assume the video is streaming.
                    // We will now set the camera status to WAITING_FOR_VIDEO. We are not going to set it to STREAMING yet until we get video
                    // data from the web socket
                    changeCamerasState(ActlyzerCameraStreamState.WAITING_FOR_VIDEO);
                } else if (status === ActlyzerDeviceStatus.READY) {
                    // If device status is READY then we know for sure it is NOT streaming therefore we can change all the cameras' status
                    // to NOT_STREAMING
                    changeCamerasState(ActlyzerCameraStreamState.NOT_STREAMING);
                } else {
                    // If the status is something else, we will assume the video streaming is either being started or stopped or something else.
                    // Therefore, we will have to keep checking the status until we get the RUNNING or READY status
                    changeCamerasState(ActlyzerCameraStreamState.START_STREAMING_REQUESTED);
                    sendCommand(
                        statusCommandInfo.deviceId,
                        statusCommandInfo.settings.command,
                        statusCommandInfo.payload,
                        props.customSettings?.checkStatusDelayInSecs ?? GET_STATUS_DELAY_IN_SECS,
                    );
                }
            }
        };
    }, [startCommandInfo, stopCommandInfo, statusCommandInfo, handleDeviceErrorStatus, changeCamerasState, sendCommand,
        props.customSettings?.checkStatusDelayInSecs]);



    /**
     * On component loads, we will create a web socket connection to the device and continuously listen to events from the socket so that we
     * can get results for commands we send to start/end video stream.
     */
    useEffect(() => {

        const homeId = selectedEntity?.homeId;

        // Create a WebSocket connection once we have fetched DEVICE and AgentLoginInfo
        const wsObservable = streamVideoInitiated && homeId && statusCommandInfo && agentLoginInfo?.xsrfGuard
            ? RitzWebSocketFactory.getInstance().createObservableWebSocket(homeId, {
                authToken: agentLoginInfo?.xsrfGuard,
            }) : undefined;

        wsObservable?.subscribe(deviceWebsocketObserver);

        // Send a CHECK STATUS command once just to get the current streaming status on component init
        if (wsObservable && statusCommandInfo) {
            sendCommand(statusCommandInfo.deviceId, statusCommandInfo.settings.command, statusCommandInfo.payload);
        }

        // Return a clean up function that will stop the web socket when the component unload
        return () => {
            wsObservable?.destroy();
        };
    }, [agentLoginInfo?.xsrfGuard, deviceWebsocketObserver, selectedEntity?.attributes, selectedEntity?.homeId, sendCommand, statusCommandInfo,
        streamVideoInitiated]);



    /**
     * This WebSocket observer is for observing messages from the web socket that is used for receiving video stream from the device
     */
    const videoStreamWebsocketObserver = useMemo((): WebSocketObserver => {
        return async (event: ModeWebSocketEvent | unknown) => {

            // The event object that come from the web socket will be a Blob, not a JSON object. We need to convert it to JSON before we can use it.
            if (event instanceof Blob) {
                const rawData = await event.text();

                if (typeof rawData === 'string') {
                    const videoData = JSON.parse(rawData);

                    if (isActlyzerVideoMessageData(videoData)) {

                        // Update all the cameras based on the data from the web socket
                        setCameras((currentCameras) => {
                            return updateCamerasData(currentCameras, videoData);
                        });
                    }
                }
            }
        };
    }, []);


    /**
     * When deviceStatus changed to RUNNING, we will create a web socket connection and continuously listen to video stream data from the web socket
     */
    useEffect(() => {

        const homeId = selectedEntity?.homeId;
        const deviceId = selectedEntity?.attributes?.[ENTITY_DEVICE_ID_ATTRIBUTE];

        const wsObservable = deviceStatus === ActlyzerDeviceStatus.RUNNING && projectKey && selectedEntity && homeId && deviceId
            ? WebSocketFactory.getInstance().createObservableWebSocket({
                authToken  : projectKey.token,
                path       : `/homes/${homeId}/appProxy/actlyzer/websocket/devices/${deviceId}`,
                otherParams: {
                    realtime: true,
                },
            }) : undefined;

        wsObservable?.subscribe(videoStreamWebsocketObserver);

        // Return a clean up function that will stop the web socket when the component unload
        return () => {
            wsObservable?.destroy();
        };
    }, [deviceStatus, projectKey, selectedEntity, videoStreamWebsocketObserver]);


    /**
     * When the user leave the page and the component is destroyed, we will send a command to STOP streaming.
     */
    useEffect(() => {

        // When the component start up, check if there is a task to stop streaming for the device associated with this component.
        const existingStopStreamingTask = stopCommandInfo ? stopStreamingTasksByDeviceId[stopCommandInfo.deviceId] : undefined;


        // If there is a task in the schedule then cancel that task. The user came back to the page so we don't need to send the command for now.
        if (existingStopStreamingTask !== undefined) {
            window.clearTimeout(existingStopStreamingTask.timerId);
        }


        // Return a clean up function that will send a STOP command when the component unload
        return () => {

            // The user leave the page and the component is unloaded. We need to schedule a task to send a STOP command to the device
            // to stop streaming. HOWEVER, we only need to do it if the user initiated the START streaming or there is a previous task to
            // stop streaming.
            if (stopCommandInfo) {

                // Check if there is a task to stop streaming for the device associated with this component.
                const existingStopStreamingTask = stopStreamingTasksByDeviceId[stopCommandInfo.deviceId];
                if (existingStopStreamingTask) {

                    const commandInfo = existingStopStreamingTask.commandInfo;

                    // Schedule it to send the command in N seconds
                    const timerId = window.setTimeout(() => {
                        // N seconds passed and the user did not come back to the page, we can now send the STOP command
                        sendCommand(commandInfo.deviceId, commandInfo.settings.command, commandInfo.payload);

                        // We already send the STOP command, we will delete this from the schedule now.
                        delete stopStreamingTasksByDeviceId[commandInfo.deviceId];
                    }, (props.customSettings?.sendStopCommandDelayInSecs ?? SEND_STOP_COMMAND_DEFAULT_DELAY_IN_SECONDS) * 1000);

                    // Add the task to the schedule so we remember it. If the user load this component again for the SAME device then we will
                    // stop it from sending the STOP command.
                    stopStreamingTasksByDeviceId[commandInfo.deviceId] = {
                        timerId, commandInfo,
                    };
                }
            }
        };
    }, [props.customSettings?.sendStopCommandDelayInSecs, sendCommand, stopCommandInfo]);



    /**
     * When one of the cameras wants to start streaming
     */
    const onStartStreamingHandler = useCallback(() => {
        if (startCommandInfo) {
            setStreamVideoInitiated(true);
            sendCommand(startCommandInfo.deviceId, startCommandInfo.settings.command, startCommandInfo.payload);

            // Add the task to the schedule so we remember it. If the user load this component again for the SAME device then we will
            // stop it from sending the STOP command.
            if (stopCommandInfo && props.customSettings?.sendStopCommandDelayInSecs !== DISABLE_SEND_STOP_COMMAND_SETTING_VALUE) {
                stopStreamingTasksByDeviceId[startCommandInfo.deviceId] = {
                    commandInfo: stopCommandInfo,
                };
            }
        }
    }, [props.customSettings?.sendStopCommandDelayInSecs, sendCommand, startCommandInfo, stopCommandInfo]);


    /**
     * When one of the cameras want to stop streaming
     */
    const onStopStreamingHandler = useCallback(() => {
        if (stopCommandInfo) {
            sendCommand(stopCommandInfo.deviceId, stopCommandInfo.settings.command, stopCommandInfo.payload);

            // Because the user already explicitly send a STOP command, we can delete the one in the schedule if there is one.
            delete stopStreamingTasksByDeviceId[stopCommandInfo.deviceId];
        }
    }, [sendCommand, stopCommandInfo]);


    /**
     * When one of the cameras wants to pause streaming. Pause streaming is front end feature only so we don't need to send any command
     * to the device. The videos will continue to come from the web socket but front end will ignore them
     */
    const onPauseStreamingHandler = useCallback(() => {
        changeCamerasState(ActlyzerCameraStreamState.ON_PAUSE);
    }, [changeCamerasState]);


    /**
     * When one of the cameras wants to unpause streaming.
     */
    const onUnpauseStreamingHandler = useCallback(() => {
        changeCamerasState(ActlyzerCameraStreamState.STREAMING);
    }, [changeCamerasState]);



    /**
     * Create a component for showing messages and action when the device is not streaming
     */
    const createMessageBox = useCallback(() => {
        const deviceId = selectedEntity?.attributes?.[ENTITY_DEVICE_ID_ATTRIBUTE];

        const message = (() => {
            if (deviceStatus === ActlyzerDeviceStatus.READY) {
                return 'video_streaming.state.not_streaming';
            }
            if (deviceStatus === ActlyzerDeviceStatus.STARTING) {
                return 'video_streaming.state.start_streaming_requested';
            }
            if (deviceStatus === ActlyzerDeviceStatus.RUNNING) {
                return 'video_streaming.state.waiting_for_video';
            }
            return 'label.loading';
        })();

        return (
            <Paper elevation={2} className={classes.notStreamingMessage}>
                {deviceStatus === ActlyzerDeviceStatus.READY ? (
                    <Icon className="icon">videocam_off</Icon>
                ) : (
                    <CircularProgress className="icon" />
                )}
                <Typography className="message" variant="h5">
                    {trans(message)}
                </Typography>

                {deviceStatus === ActlyzerDeviceStatus.READY && deviceId ? (
                    <Button
                        className="action"
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                            onStartStreamingHandler();
                        }}
                    >
                        {trans('label.start')}
                    </Button>
                ) : deviceStatus !== ActlyzerDeviceStatus.STARTING && deviceId ? (
                    // The device is RUNNING which mean it is streaming. However, we probably don't have data yet.
                    <Button
                        className="action"
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                            onStopStreamingHandler();
                        }}
                    >
                        {trans('label.cancel')}
                    </Button>
                ) : (
                    <></>
                )}
            </Paper>
        );
    }, [classes.notStreamingMessage, deviceStatus, onStartStreamingHandler, onStopStreamingHandler, selectedEntity?.attributes, trans]);


    return (
        <>
            {selectedEntity && (
                <>
                    {cameras && cameras.length > 0 ? (
                        <ExtGrid
                            className={clsx(props.className, classes.root)}
                            container
                            xs_spacing={1}
                            md_spacing={2}
                            lg_spacing={3}
                        >
                            {cameras.map((cameraInfo) => {
                                return (
                                    <Grid
                                        key={`${cameraInfo.cameraId}`}
                                        item
                                        xs={props.customSettings?.gridSizes?.xs ?? 12}
                                        sm={props.customSettings?.gridSizes?.sm}
                                        md={props.customSettings?.gridSizes?.md}
                                        lg={props.customSettings?.gridSizes?.lg}
                                        xl={props.customSettings?.gridSizes?.xl}
                                    >
                                        <ActlyzerCameraCard
                                            className={classes.videoCard}
                                            projectId={props.projectId}
                                            frameRate={props.customSettings?.frameRate}
                                            showVisitorsCount={props.customSettings?.showVisitorsCount}
                                            cameraInfo={cameraInfo}
                                            onStartStreaming={() => {
                                                onStartStreamingHandler();
                                            }}
                                            onStopStreaming={() => {
                                                onStopStreamingHandler();
                                            }}
                                            onPauseStreaming={() => {
                                                onPauseStreamingHandler();
                                            }}
                                            onUnpauseStreaming={() => {
                                                onUnpauseStreamingHandler();
                                            }}
                                            onCancelStreamingRequest={() => {
                                                /**
                                                 * If the START streaming request takes too long and the user wants to cancel the requests, send
                                                 * a STOP command.
                                                 */
                                                onStopStreamingHandler();
                                            }}
                                        />
                                    </Grid>
                                );
                            })}
                        </ExtGrid>
                    ) : (
                        createMessageBox()
                    )}
                </>
            )}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'ActlyzerEntityCamerasUIComponent', ActlyzerEntityCamerasUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Actlyzer_Entity_Cameras_Comp',
    {
        componentFunc          : ActlyzerEntityCamerasUIComponent,
        customSettingsValidator: isActlyzerEntityCamerasUIComponentCustomSettings,
    },
);
